import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, Button, Table } from 'react-bootstrap'
import {
  taidot,
  translations as arrayTranslations,
  skills as modeToSkillMap,
} from '../services/arrays'
import { getTranslation } from '../services/translations'

const weekdaysFi = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la']
const weekdaysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Hattrick-kausi/viikko laskenta
// Referenssi: ma 16.2.2026 = kausi 93, viikko 11 alku (viikko vaihtuu maanantaisin)
const HT_REF_DATE = new Date(Date.UTC(2026, 1, 16)) // 16.2.2026 UTC
const HT_REF_TOTAL_WEEK = (93 - 1) * 16 + (11 - 1) // 1482

const getHtSeasonWeek = dateStr => {
  const d = new Date(dateStr)
  const utcDate = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.floor(
    (utcDate - HT_REF_DATE.getTime()) / (1000 * 60 * 60 * 24)
  )
  const diffWeeks = Math.floor(diffDays / 7)
  const totalWeek = HT_REF_TOTAL_WEEK + diffWeeks
  const season = Math.floor(totalWeek / 16) + 1
  const week = (totalWeek % 16) + 1
  return { season, week }
}

const TrainingHistory = ({ player }) => {
  const [showHistory, setShowHistory] = useState(false)
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)
  const language = lang === 'en' ? 1 : 0

  const history = (player.trainhistory || []).filter(h => h && h.date)

  if (history.length === 0) return null

  // Laske harjoitetun taidon lähtötaso ja muutos verrattuna edelliseen saman treenimuodon merkintään
  const getSkillChange = (entry, index) => {
    const mode = Number(entry.mode)
    const skillKey = modeToSkillMap[mode - 2]
    if (!skillKey || !entry.playerSkills) return null

    // Etsi edellinen merkintä samalla modella, jolla on playerSkills
    for (let i = index - 1; i >= 0; i--) {
      const prev = history[i]
      if (
        Number(prev.mode) === mode &&
        prev.playerSkills &&
        prev.playerSkills[skillKey] !== undefined
      ) {
        const startLevel = prev.playerSkills[skillKey]
        const currentLevel = entry.playerSkills[skillKey]
        const diff = currentLevel - startLevel
        return {
          skillName: arrayTranslations[skillKey]
            ? arrayTranslations[skillKey][language]
            : skillKey,
          startLevel,
          diff,
        }
      }
    }
    return null
  }

  // Muunna mode-numero suomenkieliseksi nimeksi
  const getModeName = mode => {
    const modeIndex = Number(mode) - 2
    if (modeIndex >= 0 && modeIndex < taidot.length) {
      return taidot[modeIndex]
    }
    return mode
  }

  const getStaminaShare = entry => {
    const stamina = Number(entry.stamina)
    if (!Number.isFinite(stamina)) return null

    const intensity = Number(entry.intensity)
    const effectiveIntensity = Number.isFinite(intensity) ? intensity : 100
    return (stamina * effectiveIntensity) / 100
  }

  return (
    <Card
      className="mt-3"
      style={{
        backgroundColor: '#2C2F33',
        color: '#FFFFFF',
        borderColor: '#1E2124',
      }}
    >
      <Card.Header
        style={{
          backgroundColor: '#1a1d21',
          color: '#FFFFFF',
          fontSize: '0.8rem',
          padding: '6px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {t('trainingHistory')}
        <Button
          variant="outline-light"
          size="sm"
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? t('hide') : t('show')}
        </Button>
      </Card.Header>
      {showHistory && (
        <Card.Body style={{ padding: '0.5rem' }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            variant="dark"
            style={{ fontSize: '0.8rem', marginBottom: 0 }}
          >
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('seasonWeek')}</th>
                <th>{t('trainingMode')}</th>
                <th>{t('trainingCoach')}</th>
                <th>{t('trainingAssistants')}</th>
                <th>{t('trainingMinutes')}</th>
                <th>{t('trainingIntensity')}</th>
                <th>{t('staminaShare')}</th>
                <th>{t('efficiency')}</th>
                <th>{t('staminaTrainingShare')}</th>
                <th>{t('startLevel')}</th>
                <th>{t('skillChange')}</th>
              </tr>
            </thead>
            <tbody>
              {[...history].reverse().map((entry, reverseIndex) => {
                const originalIndex = history.length - 1 - reverseIndex
                const change = getSkillChange(entry, originalIndex)
                const d = new Date(entry.date)
                const weekdays = lang === 'en' ? weekdaysEn : weekdaysFi
                const dateStr = `${weekdays[d.getDay()]} ${d.getDate()}.${
                  d.getMonth() + 1
                }.${d.getFullYear()}`
                const ht = getHtSeasonWeek(entry.date)
                return (
                  <tr key={reverseIndex}>
                    <td>{dateStr}</td>
                    <td>
                      S{ht.season}/{ht.week}
                    </td>
                    <td>{getModeName(entry.mode)}</td>
                    <td>
                      {Number.isFinite(Number(entry.coach))
                        ? Number(entry.coach)
                        : ''}
                    </td>
                    <td>
                      {Number.isFinite(Number(entry.assistants))
                        ? Number(entry.assistants)
                        : ''}
                    </td>
                    <td>
                      {Number.isFinite(Number(entry.minutes))
                        ? Number(entry.minutes)
                        : ''}
                    </td>
                    <td>
                      {Number.isFinite(Number(entry.intensity))
                        ? `${Number(entry.intensity).toFixed(0)}%`
                        : ''}
                    </td>
                    <td>
                      {Number.isFinite(Number(entry.stamina))
                        ? `${Number(entry.stamina).toFixed(0)}%`
                        : ''}
                    </td>
                    <td>{(entry.effency * 100).toFixed(0)}%</td>
                    <td>
                      {getStaminaShare(entry) !== null
                        ? `${getStaminaShare(entry).toFixed(0)}%`
                        : ''}
                    </td>
                    <td>
                      {change ? (
                        <span>
                          {change.skillName} {change.startLevel.toFixed(3)}
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td>
                      {change && change.diff !== 0 ? (
                        <span
                          style={{
                            color: change.diff > 0 ? '#43B581' : '#ff7575',
                            fontWeight: 'bold',
                          }}
                        >
                          {change.skillName} {change.diff > 0 ? '+' : ''}
                          {change.diff.toFixed(3)}
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Card.Body>
      )}
    </Card>
  )
}

export default TrainingHistory
